import { isEqual, sortBy } from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CommonModal from 'src/components/CommonModal';
import DateRangePicker from 'src/components/DateRangePicker';
import SelectBox from 'src/components/SelectBox/SelectBox';
import { getDateRange, getSelectBoxOptions } from 'src/utils/CommonFunctions';

import { classOptions } from '../AddressDetails/Address.const';

interface IProps {
    handleClose(): any;
    params: any;
    setParams: any;
    statusArr: any;
    setFilterCount: any;
}

const FilterModal = ({ handleClose, setParams, params, setFilterCount }: IProps) => {
    const { allTimeStartDateActual, appearance, isTeamLoading, allTeamListOptions, allTeamMemberListOptions } = useSelector((state: any) => state.basic);

    const { t } = useTranslation();
    const [team] = useState(params?.team ?? 'all');
    const [userSelected, setUserSelected] = useState(params.member ?? 'all');
    const [selectedUserOption, setSelectedUserOption] = useState<any>(null);
    const [classification, setClassification] = useState(params.classification ?? null);
    const [selectedTimeRange, setSelectedTimeRange] = useState<string>('custom');
    const [isPrevYearDisable, setIsPrevYearDisable] = useState<any>(false);
    const [allTimeStartDate, setAllTimeStartDate] = useState<any>('');

    const [teamMemberListOptions, setTeamMemberListOptions] = useState<any[]>([]);
    const [searchTeamMember, setSearchTeamMember] = useState<string>('');
    const [startDate, setStartDate] = useState<any>(params.startDate ? moment(params.startDate).toDate() : null);
    const [endDate, setEndDate] = useState<any>(params.endDate ? moment(params.endDate).toDate() : null);
    const initialParams = useRef(params);
    const [isFilterChanged, setIsFilterChanged] = useState(false);

    useEffect(() => {
        const startOfLastYear = moment().subtract(1, 'years').startOf('year');
        const providedDate = moment(allTimeStartDateActual);

        // for user if start of allTimeStartDateActual is less than last year's first date then not letting user go further than last year's first date
        if (providedDate.isBefore(startOfLastYear)) {
            setAllTimeStartDate(startOfLastYear.format('YYYY-MM-DD'));
        } else {
            setAllTimeStartDate(allTimeStartDateActual);
        }
    }, [allTimeStartDateActual]);

    const setTeamMemberOptions = (teamMembers: any[]) => {
        teamMembers = sortBy(teamMembers, ['firstName', 'lastName']);

        teamMembers = teamMembers.filter((item: any) => item.firstName && item.lastName);

        if (userSelected != 'all') {
            const selectedTeamMemberIndex = teamMembers.findIndex((member: any, index: number) => member.id == userSelected && index > 4);

            if (selectedTeamMemberIndex !== -1) {
                const selectedTeamMember = teamMembers[selectedTeamMemberIndex];
                teamMembers = teamMembers.slice(0, 4);
                teamMembers.unshift(selectedTeamMember);
            } else {
                teamMembers = teamMembers.slice(0, 5);
            }
        } else {
            teamMembers = teamMembers.slice(0, 5);
        }

        setTeamMemberListOptions(getSelectBoxOptions(teamMembers, 'id', 'firstName', true, 'imageUrl', 'image', undefined, 'lastName', true));
    };

    useEffect(() => {
        if (!isTeamLoading) {
            setTeamMemberOptions(allTeamMemberListOptions);
        }
    }, [isTeamLoading]);

    useEffect(() => {
        const filtersChanged = !isEqual(
            {
                member: userSelected,
                classification,
                startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : null,
                endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
            },
            {
                member: initialParams.current.member,
                classification: initialParams.current.classification,
                startDate: initialParams.current.startDate,
                endDate: initialParams.current.endDate,
            }
        );

        setIsFilterChanged(filtersChanged);
    }, [userSelected, classification, startDate, endDate]);

    useEffect(() => {
        if (searchTeamMember) {
            const delayDebounceFn = setTimeout(() => {
                const teamMembers = team === 'all' ? allTeamMemberListOptions : allTeamListOptions?.find((tm: any) => tm.id === team)?.members;
                setTeamMemberOptions(teamMembers?.filter((member: any) => `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchTeamMember.toLowerCase())) || []);
            }, 250);

            return () => clearTimeout(delayDebounceFn);
        } else {
            const teamMembers = team === 'all' ? allTeamMemberListOptions : allTeamListOptions?.find((tm: any) => tm.id === team)?.members;
            setTeamMemberOptions(teamMembers || []);
        }
    }, [searchTeamMember]);

    useEffect(() => {
        if (!team && userSelected === 'all') {
            setTeamMemberOptions(allTeamMemberListOptions);
        }

        if (userSelected) {
            const selectedMember = teamMemberListOptions.filter((user: any) => userSelected === user.value);

            setSelectedUserOption(selectedMember);
        } else {
            setSelectedUserOption(null);
        }
    }, [userSelected, teamMemberListOptions]);

    const handleDatePickerChange = (date: any, type: any) => {
        const [start, end] = date;
        setSelectedTimeRange(type);
        const twoYearsAgo = moment().subtract(1, 'years').startOf('year');
        const daysDiff = moment(end).diff(moment(start), 'days') + 1;
        const prevMonth = moment(start).subtract(daysDiff, 'day');

        if (prevMonth.isSameOrBefore(twoYearsAgo, 'days')) {
            setIsPrevYearDisable(true);
        } else {
            setIsPrevYearDisable(false);
        }

        setStartDate(start ?? null);

        if (!end) {
            setEndDate(start ?? null);
        } else {
            setEndDate(end ?? null);
        }
    };

    const getFilterRange = (type?: string) => {
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        moment.tz.setDefault(appearance?.timezone ?? browserTimezone);

        var startDT = new Date();
        var endDT = new Date();
        const currentYear = new Date().getFullYear();
        const twoYearsAgo = moment().subtract(1, 'years').startOf('year');

        if (type === 'prev' && !isPrevYearDisable) {
            if (selectedTimeRange === 'yearly') {
                startDT = moment(startDate).subtract(1, 'year').toDate();
                endDT = moment(endDate).subtract(1, 'year').toDate();
                const prevMonth = moment(moment(startDate, 'YYYY')).subtract(1, 'year').format('YYYY');

                if (currentYear - parseInt(prevMonth) >= 1) {
                    setIsPrevYearDisable(true);
                } else {
                    setIsPrevYearDisable(false);
                }
            } else if (selectedTimeRange === 'weekly') {
                startDT = moment(endDate).subtract(1, 'week').isoWeekday(0).toDate();
                endDT = moment(endDate).subtract(1, 'week').isoWeekday(6).toDate();
                const prevMonth = moment(startDate).subtract(1, 'week');

                if (prevMonth.isSameOrBefore(twoYearsAgo, 'week')) {
                    setIsPrevYearDisable(true);
                } else {
                    setIsPrevYearDisable(false);
                }
            } else if (selectedTimeRange === 'monthly') {
                startDT = moment(startDate).subtract(1, 'months').startOf('month').toDate();
                endDT = moment(startDate).subtract(1, 'months').endOf('month').toDate();
                const prevMonth = moment(startDate).subtract(1, 'months');

                if (prevMonth.isSameOrBefore(twoYearsAgo, 'month')) {
                    setIsPrevYearDisable(true);
                } else {
                    setIsPrevYearDisable(false);
                }
            } else if (selectedTimeRange === 'daily') {
                startDT = moment(startDate).subtract(1, 'days').toDate();
                endDT = moment(endDate).subtract(1, 'days').toDate();
                const prevMonth = moment(startDate).subtract(1, 'day');

                if (prevMonth.isSameOrBefore(twoYearsAgo, 'day')) {
                    setIsPrevYearDisable(true);
                } else {
                    setIsPrevYearDisable(false);
                }
            } else {
                if (selectedTimeRange === 'quarterly') {
                    startDT = moment(moment(startDate).subtract(1, 'quarters').startOf('quarter'), 'YYYY-MM-DD').toDate();
                    endDT = moment(moment(startDate).subtract(1, 'quarters').endOf('quarter'), 'YYYY-MM-DD').toDate();
                    const prevMonth = moment(startDate).subtract(1, 'quarters');

                    if (prevMonth.isSameOrBefore(twoYearsAgo, 'quarters')) {
                        setIsPrevYearDisable(true);
                    } else {
                        setIsPrevYearDisable(false);
                    }
                } else if (selectedTimeRange === 'last_year') {
                    startDT = moment(startDate).subtract(1, 'year').toDate();
                    endDT = moment(startDate).subtract(1, 'year').toDate();
                } else {
                    const daysDiff = moment(endDate).diff(moment(startDate), 'days') + 1;
                    startDT = moment(startDate).clone().subtract(daysDiff, 'days').toDate();
                    endDT = moment(endDate).clone().subtract(daysDiff, 'days').toDate();

                    const daysDiffN = moment(endDT).diff(moment(startDT), 'days') + 1;
                    const prevMonth = moment(startDT).subtract(daysDiffN, 'day');

                    if (prevMonth.isSameOrBefore(twoYearsAgo, 'days')) {
                        setIsPrevYearDisable(true);
                    } else {
                        setIsPrevYearDisable(false);
                    }
                }
            }
            setStartDate(startDT);
            setEndDate(endDT);
        } else if (type === 'next') {
            setIsPrevYearDisable(false);

            if (selectedTimeRange === 'yearly') {
                startDT = moment(startDate).add(1, 'year').toDate();
                endDT = moment(endDate).add(1, 'year').toDate();
            } else if (selectedTimeRange === 'weekly') {
                startDT = moment(endDate).add(1, 'week').isoWeekday(0).toDate();
                endDT = moment(endDate).add(1, 'week').isoWeekday(6).toDate();
            } else if (selectedTimeRange === 'monthly') {
                startDT = moment(startDate).add(1, 'months').startOf('month').toDate();
                endDT = moment(endDate).add(1, 'months').endOf('month').toDate();
            } else if (selectedTimeRange === 'daily') {
                startDT = moment(startDate).add(1, 'days').toDate();
                endDT = moment(endDate).add(1, 'days').toDate();
            } else {
                if (selectedTimeRange === 'quarterly') {
                    startDT = moment(moment(startDate).add(1, 'quarters').startOf('quarter'), 'YYYY-MM-DD').toDate();
                    endDT = moment(moment(startDate).add(1, 'quarters').endOf('quarter'), 'YYYY-MM-DD').toDate();
                } else if (selectedTimeRange === 'last_year') {
                    startDT = moment(startDate).add(1, 'year').toDate();
                    endDT = moment(startDate).add(1, 'year').toDate();
                } else {
                    const daysDiff = moment(endDate).diff(moment(startDate), 'days') + 1;
                    startDT = moment(startDate).clone().add(daysDiff, 'days').toDate();
                    endDT = moment(endDate).clone().add(daysDiff, 'days').toDate();
                }
            }
            setStartDate(startDT);
            setEndDate(endDT);
        } else {
            if (!isPrevYearDisable) {
                const { start, end } = getDateRange(appearance, selectedTimeRange);
                const daysDiff = moment(end).diff(moment(start), 'days') + 1;
                const prevMonth = moment(start).subtract(daysDiff, 'day');

                if (prevMonth?.isSameOrBefore(twoYearsAgo, 'day')) {
                    setIsPrevYearDisable(true);
                } else {
                    setIsPrevYearDisable(false);
                }
                startDT = start ?? new Date();
                endDT = end ?? new Date();
                setStartDate(startDT);
                setEndDate(endDT);
            }
        }
    };

    const handleSave = () => {
        let fCount = 0;
        if (startDate && endDate) fCount += 1;
        if (userSelected !== 'all') fCount += 1;
        if (classification && classification !== 'all') fCount += 1;

        setFilterCount(fCount);
        let startDT = startDate
            ? selectedTimeRange === 'custom'
                ? moment(startDate)
                      // .tz(appearance?.timezone ?? browserTimezone)
                      .local()
                      .format('YYYY-MM-DD')
                : moment
                      .utc(startDate)
                      // .tz(appearance?.timezone ?? browserTimezone)
                      .local()
                      .format('YYYY-MM-DD')
            : null;
        let endDT = endDate
            ? selectedTimeRange === 'custom'
                ? moment(endDate)
                      // .tz(appearance?.timezone ?? browserTimezone)
                      .local()
                      .format('YYYY-MM-DD')
                : moment
                      .utc(endDate)
                      // .tz(appearance?.timezone ?? browserTimezone)
                      .local()
                      .format('YYYY-MM-DD')
            : null;
        setParams((old: any) => ({
            ...old,
            page: 1,
            member: userSelected ?? 'all',
            startDate: startDT,
            endDate: endDT,
            classification: classification,
        }));
        handleClose();
    };

    return (
        <CommonModal
            size={'smd:max-w-[490px] max-w-[336px] overflow-unset'}
            handleClose={handleClose}
            title={t('Common.Filters')}
            primaryBtnText={t('Common.Save')}
            primaryBtnDisabled={!isFilterChanged}
            secondaryBtnText={t('Common.Cancel')}
            secondaryBtnOnClick={handleClose}
            primaryBtnOnClick={handleSave}
            isOverflow={false}
        >
            <div className="p-5">
                <div className="rounded-lg border border-utilityGray200 p-3 flex flex-col gap-3">
                    {window?.MODE !== 'production' && (
                        <div className="smd:flex flex-wrap items-center smd:flex-none">
                            <label className={`form_label smd:mb-0 mb-1.5 w-24`}>{t('Common.Select_Type')}</label>
                            <SelectBox
                                name="classification"
                                required
                                className={`form_control`}
                                labelClassName="form_label mb-1.5 block"
                                options={classOptions}
                                placeholder={t('QuotingHub.QuotingAnalytics.All_types')}
                                isClearable={true}
                                value={classification ? classOptions?.filter((val: any) => classification === val?.value) : 'All'}
                                onChangeFunc={(event: any) => setClassification(event?.value)}
                                parentClassName="flex-1"
                            />
                        </div>
                    )}
                    <div className="smd:flex flex-wrap items-center">
                        <label className={`form_label smd:mb-0 mb-1.5 w-24`}>{t('Common.Created_Date')}</label>
                        <div className="items-center cursor-pointer justify-between flex flex-1" key={`datepicker_${startDate}_${endDate}`}>
                            <DateRangePicker
                                handleDatePickerChange={(dates: any, type?: string) => handleDatePickerChange(dates, type)}
                                selectedTimeRange={selectedTimeRange}
                                startDate={startDate}
                                endDate={endDate}
                                allTimeStartDate={allTimeStartDate}
                                showYearDropdown={false}
                                isShowDropDownIcon={false}
                                minDate={moment().subtract(1, 'years').startOf('year').toDate()}
                                containerClassName="!mt-0"
                                datePickerAsModal={true}
                                parentClassName="sales-datepicker-quote"
                                isPrevDisabled={isPrevYearDisable}
                                onPrevNextClick={(type: string) => getFilterRange(type)}
                                textWrapClass="!justify-start"
                                calendarClass="!hidden"
                                dateRangeWrapClass="!shadow-none"
                                placeholder={t('QuotingHub.QuotingDashboard.Please_select_date')}
                                isClearable={true}
                            />
                        </div>
                    </div>
                    <div className="smd:flex items-center smd:flex-none flex-1 min-h-[40px] ">
                        {/* IF YOU CHANGE CLASS IN BELOW LABLE, CHANGE WIDTH IN SELECTBOX AS WELL */}
                        <label className={`form_label smd:mb-0 mb-1.5 w-24`}>{t('Common.Users')}</label>
                        <SelectBox
                            parentClassName="w-[calc(100%_-_96px)]"
                            name="userListGroup"
                            id="userListGroup"
                            size="sm"
                            placeholder={t('QuotingHub.QuotingAnalytics.All_Users')}
                            className="form_control"
                            isClearable={true}
                            isSearchable={true}
                            options={teamMemberListOptions}
                            noOptionMessage={t('QuotingHub.QuotingAnalytics.No_Users_Found')}
                            onChangeFunc={(event: any, actionMeta: any) => {
                                setUserSelected(event?.value ?? 'all');

                                if (actionMeta?.action === 'clear') {
                                    setSearchTeamMember('');
                                }
                            }}
                            onInputChange={(e: any, { action }: any) => {
                                if (action === 'input-change' || action === 'input-blur') {
                                    setSearchTeamMember(e);
                                }
                            }}
                            value={selectedUserOption}
                            menuPlaceOption={'top'}
                        />
                    </div>
                </div>
            </div>
        </CommonModal>
    );
};

export default FilterModal;
